import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  TrendingDown,
  MessageSquare,
  Lightbulb,
  DollarSign,
  Shield,
  Phone,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { DECISION_TYPES } from '@/data/templates/decision-types'
import { PreReadComposer } from '@/components/composer/PreReadComposer'

const iconMap: Record<string, LucideIcon> = {
  TrendingDown,
  MessageSquare,
  Lightbulb,
  DollarSign,
  Shield,
  Phone,
}

export function PreReadComposerPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [initialDecisionType, setInitialDecisionType] = useState<string | undefined>(undefined)

  const handleComplete = (preReadId: string) => {
    navigate(`/app/projects/${projectId}/echo-panel`)
    // preReadId available for downstream use if needed
    void preReadId
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl pb-20 md:pb-6">
      <h1
        className="text-2xl font-medium text-deep-ink"
        style={{ letterSpacing: '-0.02em' }}
      >
        New pre-read
      </h1>

      {/* Decision type cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {DECISION_TYPES.map(dt => {
          const Icon = iconMap[dt.icon]
          const isSelected = initialDecisionType === dt.id

          return (
            <button
              key={dt.id}
              data-testid="decision-card"
              data-decision={dt.id}
              onClick={() => setInitialDecisionType(dt.id)}
              className={cn(
                'text-left rounded-xl border p-4 transition-colors space-y-2',
                isSelected
                  ? 'border-echo-indigo bg-indigo-50 shadow-sm'
                  : 'border-border-gray bg-white hover:border-echo-indigo/50 hover:bg-indigo-50/30'
              )}
            >
              <div
                className={cn(
                  'w-8 h-8 rounded-lg flex items-center justify-center',
                  isSelected ? 'bg-echo-indigo text-white' : 'bg-soft-panel text-slate-500'
                )}
              >
                {Icon && <Icon size={16} />}
              </div>
              <div>
                <p className="text-sm font-medium text-deep-ink">{dt.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">{dt.description}</p>
              </div>
              <p className="text-xs text-slate-400 italic leading-snug">
                "{dt.step1_question}"
              </p>
            </button>
          )
        })}
      </div>

      {/* Composer */}
      <PreReadComposer
        projectId={projectId ?? ''}
        onComplete={handleComplete}
        initialDecisionType={initialDecisionType}
      />
    </div>
  )
}
